import { ArrowLeft, X } from "lucide-react";
import { ReactNode } from "react";

import {
  IgCoreTextColors,
  IgGrayPrimitives,
} from "@intelligems/ig-design-tokens/constants";
import { IgCheckbox } from "@intelligems/ig-theme/IgCheckbox";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { useTheme } from "@mui/material";
import { surveyCheckboxSx } from "@src/features/ai/components/WorkflowEditor/execution/panel/surveyCheckboxSx";

/**
 * The pieces of the Figma "Survey card" (Atlas Component Library 173:42 /
 * 465:259) every card-shaped ask is built from — the agent's parked question
 * (`SurveyCard`) and the panel's own offers (the cart picker) alike, so the
 * two can never drift into two looks.
 */

/**
 * The card chrome: the 12px-radius low-shadow surface and the 12/12/8-padded
 * header holding the title (Body 2 semibold, Text/Core/Primary), an optional
 * status and stepper, and the dismiss X — controls centred on the title's
 * first line whether it wraps or not. The card closes on 8px of bottom
 * padding unless a button footer brings its own 12px.
 *
 * `tone="subtle"` is the chat combobox's Border/Core/Disabled hairline; every
 * other card keeps Border/Core/Default.
 */
export const SurveyCardShell = ({
  title,
  stepper,
  disabled,
  hasFooter = false,
  tone = "default",
  dismissLabel = "Dismiss question",
  onDismiss,
  testId = "survey-card",
  children,
}: {
  title: ReactNode;
  stepper?: ReactNode;
  disabled: boolean;
  /** A button footer brings its own 12px bottom padding, so the card adds none. */
  hasFooter?: boolean;
  tone?: "default" | "subtle";
  dismissLabel?: string;
  onDismiss: () => void;
  testId?: string;
  children: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Stack
      data-testid={testId}
      sx={{
        backgroundColor: theme.igBackgroundColors.baseLightHigh,
        border: `1px solid ${
          tone === "subtle"
            ? theme.igBorders.core.disabled
            : theme.igBorders.core.default
        }`,
        borderRadius: "12px",
        boxShadow: theme.igShadows.low,
        overflow: "hidden",
        paddingBottom: hasFooter ? 0 : 2,
        // Stacked over the chat rail's resize handle (zIndex 1).
        position: "relative",
        zIndex: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        sx={{ px: 3, pt: 3, pb: 2 }}
      >
        <IgTypography
          variant="body2"
          sx={{
            color: IgCoreTextColors.primary,
            flex: 1,
            fontWeight: 600,
            lineHeight: "18px",
            minWidth: 0,
            whiteSpace: "pre-line",
          }}
        >
          {title}
        </IgTypography>
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          flexShrink={0}
          sx={{ height: 18 }}
        >
          {stepper}
          <IgIconButton
            aria-label={dismissLabel}
            size="small"
            disabled={disabled}
            onClick={onDismiss}
            sx={{ p: 0 }}
          >
            <X size={16} color={theme.igIconColors.light} />
          </IgIconButton>
        </Stack>
      </Stack>
      {children}
    </Stack>
  );
};

/** Header status text: Body 3 at a 15px line in Text/Core/Tertiary. */
export const SurveyCardStatus = ({ children }: { children: ReactNode }) => (
  <IgTypography
    variant="body3"
    sx={{
      color: IgCoreTextColors.tertiary,
      lineHeight: "15px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </IgTypography>
);

/**
 * The card's link footer (Figma `Footer` on): an edge-to-edge
 * Border/Core/Disabled hairline, 8px, then one Body 2 regular action in
 * Text/Interactive/Neutral/Default padded 12px at the sides — with a 16px
 * Icon/arrow-left before it, 4px away, when `arrow` is set. The card's own
 * 8px bottom padding sits under it.
 */
export const SurveyCardFooterLink = ({
  label,
  arrow = false,
  disabled = false,
  onClick,
}: {
  label: string;
  arrow?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => {
  const theme = useTheme();
  return (
    <Stack gap={2}>
      <Divider sx={{ borderColor: theme.igBorders.core.disabled }} />
      <ButtonBase
        disableRipple
        disabled={disabled}
        onClick={onClick}
        sx={{
          alignSelf: "flex-start",
          gap: 1,
          justifyContent: "flex-start",
          px: 3,
          textAlign: "left",
        }}
      >
        {arrow && (
          <ArrowLeft size={16} color={theme.igIconColors.light} aria-hidden />
        )}
        <IgTypography
          variant="body2"
          sx={{
            color: IgCoreTextColors.interactive.neutral.default,
            lineHeight: "18px",
          }}
        >
          {label}
        </IgTypography>
      </ButtonBase>
    </Stack>
  );
};

/**
 * The option rows' own column, inset 8px inside the card. A divided footer
 * adds 8px below the rows so the last hover fill does not touch its border.
 */
export const SurveyOptionList = ({
  children,
  hasDividedFooter = false,
}: {
  children: ReactNode;
  hasDividedFooter?: boolean;
}) => (
  <Stack
    data-testid="survey-option-list"
    sx={{ px: 2, pb: hasDividedFooter ? 2 : 0 }}
  >
    {children}
  </Stack>
);

const OptionText = ({
  title,
  subline,
}: {
  title: ReactNode;
  subline?: string;
}) => (
  <Stack gap={1} sx={{ flex: 1, minWidth: 0 }}>
    <IgTypography
      variant="body2"
      sx={{ color: IgCoreTextColors.primary, lineHeight: "18px" }}
    >
      {title}
    </IgTypography>
    {subline && (
      <IgTypography
        variant="body3"
        sx={{ color: IgCoreTextColors.tertiary, lineHeight: "14px" }}
      >
        {subline}
      </IgTypography>
    )}
  </Stack>
);

function optionRowSx(
  theme: Theme,
  pressed: boolean,
  disabled: boolean
): SystemStyleObject<Theme> {
  // Figma's pressed fill; the semantic `interactive.neutral.active` token is a
  // different gray, so the primitive is the faithful mapping.
  const backgroundColor = pressed ? IgGrayPrimitives[400] : "transparent";
  let hoverBackgroundColor = "transparent";
  if (!disabled) {
    hoverBackgroundColor = pressed
      ? backgroundColor
      : theme.igBackgroundColors.interactive.neutral.hover;
  }

  return {
    alignItems: "flex-start",
    backgroundColor,
    borderRadius: "6px",
    display: "flex",
    gap: 2,
    justifyContent: "flex-start",
    p: 2,
    textAlign: "left",
    width: "100%",
    "&:hover": { backgroundColor: hoverBackgroundColor },
  };
}

/**
 * Figma "Survey option" (173:22), single-select: one click is the answer. An
 * 8px-padded, 6px-radius row — 36px tall, 52px with a subline — holding a 20px
 * number badge (gray/06, its digit Body 2 in Text/Core/Tertiary) 8px before
 * the Body 2 title.
 */
export const SurveyNumberedOption = ({
  index,
  title,
  subline,
  pressed = false,
  disabled = false,
  onClick,
}: {
  index: number;
  title: ReactNode;
  subline?: string;
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => {
  const theme = useTheme();
  return (
    <ButtonBase
      disableRipple
      disabled={disabled}
      onClick={onClick}
      sx={[
        optionRowSx(theme, pressed, disabled),
        { "&:active": { backgroundColor: IgGrayPrimitives[400] } },
      ]}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: IgGrayPrimitives[500],
          borderRadius: "6px",
          flexShrink: 0,
          height: 20,
          width: 20,
        }}
      >
        <IgTypography
          variant="body2"
          sx={{ color: IgCoreTextColors.tertiary, lineHeight: "18px" }}
        >
          {index + 1}
        </IgTypography>
      </Stack>
      <OptionText title={title} subline={subline} />
    </ButtonBase>
  );
};

/**
 * Figma "Survey option" Type=Checkbox: the multi-select row — the same row
 * with the 16px checkbox in the badge's place; toggling assembles the set
 * the card's Confirm sends.
 */
export const SurveyCheckboxOption = ({
  title,
  subline,
  checked,
  disabled = false,
  onToggle,
}: {
  title: ReactNode;
  subline?: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) => {
  const theme = useTheme();
  return (
    <Stack
      component="label"
      direction="row"
      sx={[
        optionRowSx(theme, false, disabled),
        { alignItems: "center", cursor: disabled ? "default" : "pointer" },
      ]}
    >
      <IgCheckbox
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        size="small"
        sx={surveyCheckboxSx}
      />
      <OptionText title={title} subline={subline} />
    </Stack>
  );
};
